import type { ArchiveBound, ArchiveDay, ArchiveLevel } from '@arrow/game-engine';
import {
	DEFAULT_LEVEL,
	isClueDirection,
	type Issue,
	isLevel,
	type Level,
	parsePuzzle,
	PUZZLE_TYPES,
	type Puzzle,
	type PuzzleShape,
	type PuzzleType,
	validatePuzzle,
} from '@arrow/puzzle-schema';

import { API_URL } from '../env.ts';
import { browserStore } from '../storage/kv.ts';
import { noteDaily, noteScheduleStart } from '../storage/schedule-start.ts';

/**
 * The Sparrow api (codename arrow), typed. Reads (puzzles) validate everything they hand back; writes
 * (players, solves, events) never throw and report `{ ok, status }`, so play never
 * waits on, or breaks because of, the network.
 */

/** One daily (a date at a level), its puzzle parsed and validated. */
export interface DailyPuzzle {
	/** `YYYY-MM-DD`. */
	date: string;
	/** The public "Sparrow No. n", shared by the date's levels. */
	number: number;
	level: Level;
	/** 1–5. */
	difficulty: number;
	/** The puzzle's content version. */
	version: number;
	puzzle: Puzzle;
}

/** One level of a day, without the grid (safe to show before playing). */
export interface DailyLevelSummary {
	level: Level;
	difficulty: number;
	width: number;
	height: number;
	puzzleId: string;
}

/** The levels a date offers, easy → hard. Older days (from before levels) have `medium` only. */
export interface DailyLevels {
	date: string;
	number: number;
	levels: DailyLevelSummary[];
}

/** An entry of the Play list (no grid data). */
export interface PracticeSummary {
	id: string;
	title: string;
	level: Level;
	difficulty: number;
	width: number;
	height: number;
	/** Structure only (no letters, no clue words): what the cover is drawn from. */
	shape: PuzzleShape;
}

export interface PracticePuzzle {
	id: string;
	level: Level;
	difficulty: number;
	version: number;
	puzzle: Puzzle;
}

/** A dev puzzle-switcher entry (dev/api.ts). */
export interface PuzzleSummary {
	id: string;
	title: string;
	difficulty: number;
}

export type LoadError =
	| { kind: 'network'; message: string }
	| { kind: 'not-found'; message: string }
	| { kind: 'invalid'; message: string; issues: Issue[] };

export class ApiError extends Error {
	constructor(readonly detail: LoadError) {
		super(detail.message);
	}
}

export async function getJson(path: string, signal: AbortSignal | undefined, notFound: string, headers: Record<string, string> = {}): Promise<unknown> {
	let res: Response;
	try {
		res = await fetch(`${API_URL}${path}`, { signal, headers: { Accept: 'application/json', ...headers } });
	} catch (err) {
		if (err instanceof DOMException && err.name === 'AbortError') throw err;
		throw new ApiError({ kind: 'network', message: 'Could not reach the Sparrow server.' });
	}
	if (res.status === 404) throw new ApiError({ kind: 'not-found', message: notFound });
	if (!res.ok) throw new ApiError({ kind: 'network', message: `The Sparrow server answered ${res.status}.` });
	try {
		return (await res.json()) as unknown;
	} catch {
		throw new ApiError({ kind: 'network', message: 'The Sparrow server sent something unreadable.' });
	}
}

/** Parses and validates untrusted puzzle JSON; throws an `invalid` ApiError on any issue. */
export function toPlayablePuzzle(raw: unknown): Puzzle {
	const parsed = parsePuzzle(raw);
	const issues = parsed.ok ? validatePuzzle(parsed.puzzle) : parsed.issues;
	if (!parsed.ok || issues.length > 0) {
		throw new ApiError({ kind: 'invalid', message: 'This puzzle failed validation.', issues });
	}
	return parsed.puzzle;
}

export function field(obj: unknown, key: string): unknown {
	return typeof obj === 'object' && obj !== null ? (obj as Record<string, unknown>)[key] : undefined;
}

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
const malformed = (what: string) =>
	new ApiError({ kind: 'invalid', message: `The ${what} response was malformed.`, issues: [] });

function toDaily(body: unknown): DailyPuzzle {
	const date = field(body, 'date');
	const number = field(body, 'number');
	// A pre-levels api answers without `level`/`version`: that's a medium daily, version 1.
	const level = field(body, 'level') ?? DEFAULT_LEVEL;
	const difficulty = field(body, 'difficulty');
	const version = field(body, 'version') ?? 1;
	if (typeof date !== 'string' || !isInt(number) || !isLevel(level) || !isInt(version)) throw malformed('daily');
	const puzzle = toPlayablePuzzle(field(body, 'puzzle'));
	// Every daily says where the schedule starts (one number a day): kept for Stats and Today.
	noteDaily(date, number, browserStore());
	return { date, number, level, difficulty: isInt(difficulty) ? difficulty : puzzle.difficulty, version, puzzle };
}

/** Which levels `dateKey` (the player's local date) has: what exists, so maybe only `medium`. */
export async function fetchDailyLevels(dateKey: string, signal?: AbortSignal): Promise<DailyLevels> {
	const body = await getJson(
		`/v1/daily/${encodeURIComponent(dateKey)}/levels`,
		signal,
		'No puzzle is published for this day yet.',
	);
	const date = field(body, 'date');
	const number = field(body, 'number');
	const rawLevels = field(body, 'levels');
	if (typeof date !== 'string' || !isInt(number) || !Array.isArray(rawLevels)) throw malformed('levels');
	const levels = rawLevels.flatMap((item): DailyLevelSummary[] => {
		const level = field(item, 'level');
		const difficulty = field(item, 'difficulty');
		const width = field(item, 'width');
		const height = field(item, 'height');
		const puzzleId = field(item, 'puzzleId');
		// An unknown level (from a newer api) is skipped rather than failing the day.
		return isLevel(level) && isInt(difficulty) && isInt(width) && isInt(height) && typeof puzzleId === 'string'
			? [{ level, difficulty, width, height, puzzleId }]
			: [];
	});
	if (levels.length === 0) throw malformed('levels');
	noteDaily(date, number, browserStore());
	return { date, number, levels };
}

/**
 * The daily for `dateKey` (the player's local date) at `level` (default medium).
 * The two-argument `(dateKey, signal)` form is the pre-levels call and means medium.
 */
export function fetchDaily(dateKey: string, level?: Level, signal?: AbortSignal): Promise<DailyPuzzle>;
/** @deprecated Pass a level: `fetchDaily(dateKey, level, signal)`. */
export function fetchDaily(dateKey: string, signal?: AbortSignal): Promise<DailyPuzzle>;
export async function fetchDaily(
	dateKey: string,
	levelOrSignal?: Level | AbortSignal,
	maybeSignal?: AbortSignal,
): Promise<DailyPuzzle> {
	// The pre-levels form asks without `?level=`, which the api answers with medium.
	const legacy = typeof levelOrSignal !== 'string';
	const signal = legacy ? (levelOrSignal ?? maybeSignal) : maybeSignal;
	const query = legacy ? '' : `?level=${levelOrSignal}`;
	const body = await getJson(
		`/v1/daily/${encodeURIComponent(dateKey)}${query}`,
		signal,
		'No puzzle is published for this day yet.',
	);
	return toDaily(body);
}

/** "Sparrow No. n" at `level`: what a share link opens. `not-found` if it isn't out yet. */
export async function fetchDailyByNumber(
	n: number,
	level: Level = DEFAULT_LEVEL,
	signal?: AbortSignal,
): Promise<DailyPuzzle> {
	if (!Number.isInteger(n) || n < 1) throw new ApiError({ kind: 'not-found', message: `There is no Sparrow No. ${n}.` });
	const body = await getJson(`/v1/daily/number/${n}?level=${level}`, signal, `Sparrow No. ${n} isn’t out yet.`);
	return toDaily(body);
}

/** The Play list, easiest first. Malformed entries are skipped. */
export async function fetchPractice(signal?: AbortSignal): Promise<PracticeSummary[]> {
	const body = await getJson('/v1/practice', signal, 'There are no practice puzzles yet.');
	if (!Array.isArray(body)) throw malformed('practice');
	return body.flatMap((item): PracticeSummary[] => {
		const id = field(item, 'id');
		const title = field(item, 'title');
		const level = field(item, 'level');
		const difficulty = field(item, 'difficulty');
		const width = field(item, 'width');
		const height = field(item, 'height');
		const shape = toShape(field(item, 'shape'), width, height);
		return typeof id === 'string' &&
			typeof title === 'string' &&
			isLevel(level) &&
			isInt(difficulty) &&
			isInt(width) &&
			isInt(height)
			? [{ id, title, level, difficulty, width, height, shape }]
			: [];
	});
}

/**
 * A listing's shape, checked: rows of `_ # .` only and arrows inside the grid. Anything
 * else (or an older api without it) reads as a plain grid of letter cells, so a cover
 * still draws and never shows anything it shouldn't.
 */
export function toShape(raw: unknown, width: unknown, height: unknown): PuzzleShape {
	const w = isInt(width) ? width : 0;
	const h = isInt(height) ? height : 0;
	const blank: PuzzleShape = { grid: Array.from({ length: h }, () => '_'.repeat(w)), arrows: [] };
	const grid = field(raw, 'grid');
	const arrows = field(raw, 'arrows');
	if (!Array.isArray(grid) || grid.length !== h || !grid.every((r) => typeof r === 'string' && r.length === w && /^[_#.]*$/.test(r))) return blank;
	if (!Array.isArray(arrows)) return { grid: grid as string[], arrows: [] };
	const ok = arrows.filter(
		(a): a is [number, number, PuzzleShape['arrows'][number][2]] =>
			Array.isArray(a) && isInt(a[0]) && isInt(a[1]) && a[0] >= 0 && a[0] < h && a[1] >= 0 && a[1] < w && isClueDirection(a[2]),
	);
	return { grid: grid as string[], arrows: ok.map(([r, c, d]) => [r, c, d]) };
}

const isPuzzleType = (v: unknown): v is PuzzleType => (PUZZLE_TYPES as readonly unknown[]).includes(v);

/** `GET /v1/archive`: the released past dailies in a range (default: the latest 92 days). */
export interface Archive {
	earliest: ArchiveBound | null;
	latest: ArchiveBound | null;
	days: ArchiveDay[];
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

function toBound(raw: unknown): ArchiveBound | null {
	const date = field(raw, 'date');
	const number = field(raw, 'number');
	return typeof date === 'string' && DATE_KEY.test(date) && isInt(number) ? { date, number } : null;
}

export async function fetchArchive(range: { from?: string; to?: string } = {}, signal?: AbortSignal): Promise<Archive> {
	const q = new URLSearchParams();
	if (range.from) q.set('from', range.from);
	if (range.to) q.set('to', range.to);
	const body = await getJson(`/v1/archive${q.size ? `?${q}` : ''}`, signal, 'The archive isn’t there.');
	const days = field(body, 'days');
	if (!Array.isArray(days)) throw malformed('archive');
	const earliest = toBound(field(body, 'earliest'));
	// The archive's first day: before it, local records are left out (storage/schedule-start.ts).
	if (earliest) noteScheduleStart(earliest, browserStore());
	return {
		earliest,
		latest: toBound(field(body, 'latest')),
		days: days.flatMap((d): ArchiveDay[] => {
			const date = field(d, 'date');
			const number = field(d, 'number');
			const levels = field(d, 'levels');
			if (typeof date !== 'string' || !DATE_KEY.test(date) || !isInt(number) || !Array.isArray(levels)) return [];
			return [
				{
					date,
					number,
					levels: levels.flatMap((l): ArchiveLevel[] => {
						const level = field(l, 'level');
						const difficulty = field(l, 'difficulty');
						const width = field(l, 'width');
						const height = field(l, 'height');
						const puzzleId = field(l, 'puzzleId');
						const version = field(l, 'version');
						if (!isLevel(level) || !isInt(difficulty) || !isInt(width) || !isInt(height) || typeof puzzleId !== 'string') return [];
						const type = field(l, 'type');
						const shape = toShape(field(l, 'shape'), width, height);
						const base = { level, difficulty, width, height, puzzleId, version: isInt(version) ? version : 1, shape };
						// A type the app doesn't know is left off: the puzzle is an ordinary one here.
						return [isPuzzleType(type) ? { ...base, type } : base];
					}),
				},
			];
		}),
	};
}

/** One practice puzzle, parsed and validated. */
export async function fetchPracticePuzzle(id: string, signal?: AbortSignal): Promise<PracticePuzzle> {
	const body = await getJson(`/v1/practice/${encodeURIComponent(id)}`, signal, 'That puzzle isn’t available.');
	const puzzle = toPlayablePuzzle(field(body, 'puzzle'));
	const level = field(body, 'level');
	const difficulty = field(body, 'difficulty');
	const version = field(body, 'version');
	return {
		id: puzzle.id,
		level: isLevel(level) ? level : (puzzle.level ?? DEFAULT_LEVEL),
		difficulty: isInt(difficulty) ? difficulty : puzzle.difficulty,
		version: isInt(version) ? version : 1,
		puzzle,
	};
}

// ---------------------------------------------------------------------------
// Writes (never throw)
// ---------------------------------------------------------------------------

/** A write's outcome. `status` 0 = it never reached the server (offline, blocked, CORS). */
export interface WriteResult {
	ok: boolean;
	status: number;
}

/** True for answers a retry can't fix (a bad body, an unknown puzzle): drop, don't retry. */
export function isPermanentFailure(result: WriteResult): boolean {
	return !result.ok && result.status >= 400 && result.status < 500 && result.status !== 408 && result.status !== 429;
}

/** The absolute url of an api path (for `navigator.sendBeacon`). */
export function apiUrl(path: string): string {
	return `${API_URL}${path}`;
}

/** POSTs JSON. `keepalive` lets it outlive the page. Never throws. */
export async function postJson(path: string, body: unknown, opts: { keepalive?: boolean } = {}): Promise<WriteResult> {
	try {
		const res = await fetch(apiUrl(path), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body),
			keepalive: opts.keepalive ?? false,
		});
		return { ok: res.ok, status: res.status };
	} catch {
		return { ok: false, status: 0 };
	}
}

/** `POST /v1/players`: register (or touch) this device. */
export function registerPlayer(deviceId: string, internal = false): Promise<WriteResult> {
	return postJson('/v1/players', internal ? { deviceId, internal: true } : { deviceId });
}

// ---------------------------------------------------------------------------
// Challenges ("Beat me") and the percentile (docs/SHARING-BUILD.md)
// ---------------------------------------------------------------------------

/** A challenge, as `GET /v1/challenges/:code` serves it: public, spoiler-free and anonymous. */
export interface Challenge {
	code: string;
	number: number;
	level: Level;
	/** The daily's date, `YYYY-MM-DD`. */
	date: string;
	width: number;
	height: number;
	/** The time to beat, from the challenger's server-recorded solve. */
	durationMs: number;
	checks: number;
	reveals: number;
	bestFlow: number;
	/** Encoded outcomes (`encodeOutcomes`), or null for a solve recorded without them. */
	cells: string | null;
	/** Elapsed ms at each answer solve, ascending (the pace ghost), possibly empty. */
	paceMs: number[];
	createdAt: string;
}

/** `GET /v1/challenges/:code/results`: who played the creator's challenge. */
export interface ChallengeResults {
	played: number;
	finished: number;
	/** How many beat the challenger. */
	beat: number;
	/** The finished ones (anonymous). */
	entries: { durationMs: number; beat: boolean }[];
}

/** `GET /v1/daily/number/:n/stats`: `percentile` is null until enough people have solved. */
export interface DailyStats {
	solvers: number;
	percentile: number | null;
}

/** 4–6 characters of the api's unambiguous alphabet (letters and digits). */
export const CHALLENGE_CODE = /^[A-Za-z0-9]{4,6}$/;

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** Parses an untrusted challenge body; null when it isn't one. */
export function toChallenge(body: unknown): Challenge | null {
	const code = field(body, 'code');
	const number = field(body, 'number');
	const level = field(body, 'level');
	const date = field(body, 'date');
	const width = field(body, 'width');
	const height = field(body, 'height');
	const durationMs = field(body, 'durationMs');
	const cells = field(body, 'cells');
	const pace = field(body, 'paceMs');
	if (typeof code !== 'string' || !isInt(number) || number < 1 || !isLevel(level) || typeof date !== 'string') return null;
	if (!isInt(width) || !isInt(height) || width < 1 || height < 1 || !isCount(durationMs)) return null;
	const paceMs = Array.isArray(pace) ? pace.filter(isCount).sort((a, b) => a - b) : [];
	const n = (k: string) => {
		const v = field(body, k);
		return isCount(v) ? Math.floor(v) : 0;
	};
	const created = field(body, 'createdAt');
	return {
		code,
		number,
		level,
		date,
		width,
		height,
		durationMs,
		checks: n('checks'),
		reveals: n('reveals'),
		bestFlow: n('bestFlow'),
		cells: typeof cells === 'string' && cells.length <= 400 ? cells : null,
		paceMs,
		createdAt: typeof created === 'string' ? created : '',
	};
}

/** A challenge by code. Throws `ApiError` (`not-found` for an unknown code). */
export async function fetchChallenge(code: string, signal?: AbortSignal): Promise<Challenge> {
	if (!CHALLENGE_CODE.test(code)) throw new ApiError({ kind: 'not-found', message: 'That challenge link isn’t right.' });
	const body = await getJson(`/v1/challenges/${encodeURIComponent(code.toUpperCase())}`, signal, 'That challenge has expired or never existed.');
	const challenge = toChallenge(body);
	if (!challenge) throw malformed('challenge');
	return challenge;
}

/** Who played this device's challenge. Throws `ApiError` (403 reads as `network`). */
export async function fetchChallengeResults(code: string, deviceId: string, signal?: AbortSignal): Promise<ChallengeResults> {
	const body = await getJson(
		`/v1/challenges/${encodeURIComponent(code)}/results?deviceId=${encodeURIComponent(deviceId)}`,
		signal,
		'No such challenge.',
	);
	const entries = field(body, 'entries');
	const played = field(body, 'played');
	const finished = field(body, 'finished');
	const beat = field(body, 'beat');
	if (!isCount(played) || !Array.isArray(entries)) throw malformed('results');
	return {
		played: Math.floor(played),
		finished: isCount(finished) ? Math.floor(finished) : 0,
		beat: isCount(beat) ? Math.floor(beat) : 0,
		entries: entries.flatMap((e) => {
			const durationMs = field(e, 'durationMs');
			if (!isCount(durationMs)) return [];
			// Anonymous: anything else an entry carries is dropped.
			return [{ durationMs, beat: field(e, 'beat') === true }];
		}),
	};
}

/** The percentile for a time on Sparrow No. n at `level`. Never throws: null on any failure. */
export async function fetchPercentile(n: number, level: Level, ms: number, signal?: AbortSignal): Promise<DailyStats | null> {
	try {
		if (!Number.isInteger(n) || n < 1 || !isCount(ms)) return null;
		const body = await getJson(`/v1/daily/number/${n}/stats?level=${level}&ms=${Math.round(ms)}`, signal, 'No stats.');
		const solvers = field(body, 'solvers');
		const percentile = field(body, 'percentile');
		if (!isCount(solvers)) return null;
		return {
			solvers: Math.floor(solvers),
			percentile: typeof percentile === 'number' && Number.isFinite(percentile) ? Math.max(0, Math.min(100, percentile)) : null,
		};
	} catch {
		return null;
	}
}

export type CreateChallengeResult =
	| { ok: true; code: string; url: string | null }
	/** `status` 404/409: the api has no completed solve for this device yet (sync it, retry). 0: offline. */
	| { ok: false; status: number };

/**
 * `POST /v1/challenges`: a challenge from this device's server-recorded completed solve.
 * Idempotent per (device, puzzle, level): the same code comes back. Anonymous: it sends
 * no name. Never throws.
 */
export async function createChallenge(input: { deviceId: string; number: number; level: Level }): Promise<CreateChallengeResult> {
	try {
		const res = await fetch(apiUrl('/v1/challenges'), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ deviceId: input.deviceId, number: input.number, level: input.level }),
		});
		if (!res.ok) return { ok: false, status: res.status };
		const body = (await res.json().catch(() => null)) as unknown;
		const code = field(body, 'code');
		const url = field(body, 'url');
		if (typeof code !== 'string' || !CHALLENGE_CODE.test(code)) return { ok: false, status: 502 };
		return { ok: true, code, url: typeof url === 'string' ? url : null };
	} catch {
		return { ok: false, status: 0 };
	}
}

// ---------------------------------------------------------------------------
// Carry-over (docs/mockups/install.html, Spec panel 7)
// ---------------------------------------------------------------------------

/** A one-time code for a snapshot of this device's local state (30 minutes, 3 uses). */
export interface CarryCode {
	code: string;
	expiresAt: string;
}

/** `POST /v1/carry`: a code for `snapshot` (opaque JSON, ≤ 32 KB). Null on any failure; never throws. */
export async function createCarry(deviceId: string, snapshot: unknown, signal?: AbortSignal): Promise<CarryCode | null> {
	try {
		const res = await fetch(apiUrl('/v1/carry'), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ deviceId, snapshot }),
			signal,
		});
		if (!res.ok) return null;
		const body = (await res.json().catch(() => null)) as unknown;
		const code = field(body, 'code');
		const expiresAt = field(body, 'expiresAt');
		if (typeof code !== 'string' || !/^[A-Za-z0-9]{4,8}$/.test(code)) return null;
		return { code, expiresAt: typeof expiresAt === 'string' ? expiresAt : '' };
	} catch {
		return null;
	}
}

/** `GET /v1/carry/:code`: the carried `{ deviceId, snapshot }` (untrusted: merge parses it). Null on 404 or any failure. */
export async function fetchCarry(code: string, signal?: AbortSignal): Promise<{ deviceId: unknown; snapshot: unknown } | null> {
	try {
		if (!/^[A-Za-z0-9]{4,8}$/.test(code)) return null;
		const res = await fetch(apiUrl(`/v1/carry/${encodeURIComponent(code)}`), { headers: { Accept: 'application/json' }, signal });
		if (!res.ok) return null;
		const body = (await res.json().catch(() => null)) as unknown;
		if (typeof body !== 'object' || body === null) return null;
		return { deviceId: field(body, 'deviceId'), snapshot: field(body, 'snapshot') };
	} catch {
		return null;
	}
}

/**
 * `POST /v1/carry/:code/claim` with this device's id: the api folds this device's own nests,
 * solves and push subscription into the carried id (apps/api/src/carry/device-merge.ts) and
 * returns `{ deviceId, snapshot }` as a read does. Null on 404 or any failure.
 */
export async function claimCarry(code: string, deviceId: string, signal?: AbortSignal): Promise<{ deviceId: unknown; snapshot: unknown } | null> {
	try {
		if (!/^[A-Za-z0-9]{4,8}$/.test(code)) return null;
		const res = await fetch(apiUrl(`/v1/carry/${encodeURIComponent(code)}/claim`), {
			method: 'POST',
			headers: { Accept: 'application/json', 'X-Device-Id': deviceId },
			signal,
		});
		if (!res.ok) return null;
		const body = (await res.json().catch(() => null)) as unknown;
		if (typeof body !== 'object' || body === null) return null;
		return { deviceId: field(body, 'deviceId'), snapshot: field(body, 'snapshot') };
	} catch {
		return null;
	}
}
